import { useEffect, useMemo, useRef, useState } from "react";
import { Carousel, Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import "./ads.css";
import {
  useServeAdsQuery,
  useRecordImpressionMutation,
  useRecordClickMutation,
} from "../../features/ads/adsApiSlice";
import { getAsset } from "../../helpers/helper";

const ADS_BASE = (
  process.env.REACT_APP_ADS_API_URL ||
  process.env.ADS_FLASK_API_URL ||
  "http://127.0.0.1:5393"
).replace("localhost", "127.0.0.1");

const PLACEHOLDER_RE = /^select\s/i;

const cleanPlace = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  if (!text || PLACEHOLDER_RE.test(text)) return "";
  return text;
};

const PLATFORM_ALIASES = {
  retailer: "retailerShop",
  retailershop: "retailerShop",
  wholesaler: "wholesellerShop",
  wholeseller: "wholesellerShop",
  wholesellershop: "wholesellerShop",
  manufacturer: "manufacturerShop",
  manufacturershop: "manufacturerShop",
  rawmaterial: "rawMaterialShop",
  rawmaterialshop: "rawMaterialShop",
  transport: "transportShopItem",
  transportshop: "transportShopItem",
  transportshopitem: "transportShopItem",
  farming: "farmingShop",
  farmingshop: "farmingShop",
  recycle: "recycledShop",
  recycledshop: "recycledShop",
  gem: "gemShop",
  gemshop: "gemShop",
  usedvehicle: "usedVehicleShop",
  usedvehicleshop: "usedVehicleShop",
  used: "usedShop",
  usedshop: "usedShop",
  homemade: "homemadeShop",
  homemadeshop: "homemadeShop",
  service: "services",
  services: "services",
  food: "foodShop",
  foodshop: "foodShop",
  restaurant: "foodShop",
  generalpublic: "generalPublic",
};

const normalizePlatformKey = (value) => {
  if (!value) return "";
  const key = String(value).trim();
  if (!key) return "";
  const mapped = PLATFORM_ALIASES[key.toLowerCase().replace(/[\s_-]+/g, "")];
  return mapped || key;
};

/** Map current route / shop context → ad platform key used at create time */
const resolvePlatform = (explicit, pathname = "") => {
  const fromProp = normalizePlatformKey(explicit);
  if (fromProp) return fromProp;

  // Shop context saved when opening a connected / owned shop
  const fromStorage = normalizePlatformKey(
    typeof localStorage !== "undefined"
      ? localStorage.getItem("businessType") ||
          localStorage.getItem("shopBusinessType") ||
          localStorage.getItem("OpenShopBT")
      : "",
  );
  if (fromStorage && fromStorage !== "generalPublic") return fromStorage;

  const path = (pathname || "").toLowerCase();

  if (path.includes("retailer") || path.includes("buyretailer"))
    return "retailerShop";
  if (path.includes("wholesal") || path.includes("buywholesal"))
    return "wholesellerShop";
  if (path.includes("manufactur")) return "manufacturerShop";
  if (
    path.includes("rawmaterial") ||
    path.includes("raw-material") ||
    path.includes("minerals")
  )
    return "rawMaterialShop";
  if (path.includes("transport")) return "transportShopItem";
  if (path.includes("farming")) return "farmingShop";
  if (path.includes("recycle")) return "recycledShop";
  if (path.includes("gem") || path.includes("jewel")) return "gemShop";
  if (
    path.includes("usedvehicle") ||
    path.includes("used-vehicle") ||
    path.includes("usedcars")
  )
    return "usedVehicleShop";
  if (path.includes("used") || path.includes("buyused")) return "usedShop";
  if (path.includes("homemade")) return "homemadeShop";
  if (path.includes("service")) return "services";
  if (path.includes("food") || path.includes("restaurant")) return "foodShop";
  if (path.includes("property") || path.includes("job")) return "generalPublic";

  // generalPublic = do not hard-filter by shop platform on the server
  return "generalPublic";
};

const resolveMediaUrl = (raw) => {
  if (!raw) return "";
  // Serve payloads sometimes nest path objects
  let path = raw;
  if (typeof path === "object") {
    path =
      path.url ||
      path.path ||
      path.src ||
      path.mediaPath ||
      path.media ||
      path.image ||
      "";
  }
  path = String(path || "").trim();
  if (!path) return "";

  if (
    /^https?:\/\//i.test(path) ||
    path.startsWith("data:") ||
    path.startsWith("blob:")
  ) {
    return path;
  }

  const normalized = path.startsWith("/") ? path : `/${path}`;

  // Flask ad creatives live under /uploads on the ads API host
  if (
    normalized.startsWith("/uploads/") ||
    normalized.startsWith("/static/uploads/")
  ) {
    return `${ADS_BASE.replace(/\/$/, "")}${normalized}`;
  }

  // Node-hosted shop/media assets
  if (
    normalized.startsWith("/images/") ||
    normalized.startsWith("/assets/") ||
    normalized.startsWith("images/")
  ) {
    return getAsset(path);
  }

  // Bare filename from the ads uploader → Flask uploads folder
  if (!normalized.slice(1).includes("/")) {
    return `${ADS_BASE.replace(/\/$/, "")}/uploads/${normalized.slice(1)}`;
  }

  return `${ADS_BASE.replace(/\/$/, "")}${normalized}`;
};

const ageFromDob = (dob) => {
  if (!dob) return null;
  try {
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return Math.max(age, 0);
  } catch {
    return null;
  }
};

/** Build visitor targeting from logged-in user account profile */
const buildVisitorFromUser = (user, shopLocation = {}) => {
  if (!user || typeof user !== "object") {
    return {
      country: cleanPlace(shopLocation.country),
      state: cleanPlace(shopLocation.state),
      city: cleanPlace(shopLocation.city),
      gender: "all",
      age: "",
      dob: "",
      language: "",
    };
  }

  const dob = user.dob || user.dateOfBirth || "";
  const age = ageFromDob(dob);

  // Prefer GPS/IP for live location, then profile address / marketer fields, then shop
  const country = cleanPlace(
    user.gpsLocation?.country ||
      user.ipLocation?.country ||
      user.address?.country ||
      user.MCountry ||
      shopLocation.country,
  );
  const state = cleanPlace(
    user.gpsLocation?.region ||
      user.ipLocation?.region ||
      user.address?.state ||
      user.MProvince ||
      shopLocation.state,
  );
  const city = cleanPlace(
    user.gpsLocation?.city ||
      user.ipLocation?.city ||
      user.address?.city ||
      user.MCity ||
      user.selectedCity ||
      shopLocation.city,
  );

  return {
    country,
    state,
    city,
    gender: (user.gender || "all").toLowerCase(),
    age: age != null ? age : "",
    dob: dob ? String(dob).slice(0, 10) : "",
    language: user.language || "",
  };
};

const resolveAdType = (ad) => {
  const title = (ad?.title || "").toLowerCase();
  const mediaType = (ad?.mediaType || "").toLowerCase();
  const adType = (ad?.adType || "").toLowerCase();
  if (adType === "carousel" || title === "carousel" || mediaType === "carousel")
    return "carousel";
  if (adType === "video" || title === "video" || mediaType === "video")
    return "video";
  return "banner";
};

const ShopAdds = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const impressedRef = useRef(new Set());
  const [carouselIndex, setCarouselIndex] = useState(0);

  const reduxUser = useSelector(
    (state) => state.user?.name || state.auth?.user || null,
  );
  const visitorUserId = reduxUser?._id ? String(reduxUser._id) : "";

  const placement = props.placement || "banner";
  const adTypeFilter = props.adType || "";
  const platform = resolvePlatform(
    props.businessType || props.platform,
    location.pathname,
  );

  const isOwnAd = (ad) => {
    if (!visitorUserId || !ad) return false;
    const owner = String(ad.ownerUserId || "");
    const shop = String(ad.userId || "");
    return visitorUserId === owner || visitorUserId === shop;
  };

  const shopLocation = useMemo(
    () => ({
      country: props.country || props.shopCountry || "",
      state: props.state || props.shopState || "",
      city: props.city || props.shopCity || "",
    }),
    [
      props.country,
      props.shopCountry,
      props.state,
      props.shopState,
      props.city,
      props.shopCity,
    ],
  );

  const visitor = useMemo(
    () => buildVisitorFromUser(reduxUser, shopLocation),
    [reduxUser, shopLocation],
  );

  // Stabilize query args so object identity / empty↔undefined churn does not
  // retrigger serveAds forever (looks like "refresh again and again" when no ads).
  const serveArgs = useMemo(
    () => ({
      placement: placement || "banner",
      platform: platform || "",
      adType: adTypeFilter || "",
      country: visitor.country || "",
      state: visitor.state || "",
      city: visitor.city || "",
      // "all" lets server match any gender-targeted creatives
      gender:
        visitor.gender && visitor.gender !== "all" ? visitor.gender : "all",
      age: visitor.age === 0 || visitor.age ? String(visitor.age) : "",
      dob: visitor.dob || "",
      // Do not hard-send profile language — niche ad languages were zeroing fill
      language: "",
      userId: visitorUserId || "",
    }),
    [
      placement,
      platform,
      adTypeFilter,
      visitor.country,
      visitor.state,
      visitor.city,
      visitor.gender,
      visitor.age,
      visitor.dob,
      visitorUserId,
    ],
  );

  // Always refetch on mount so a prior empty/error cache does not hide live ads.
  const { data, refetch } = useServeAdsQuery(serveArgs, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: true,
  });

  const [recordImpression] = useRecordImpressionMutation();
  const [recordClick] = useRecordClickMutation();

  useEffect(() => {
    refetch();
  }, [location.pathname, refetch]);

  const ads = useMemo(() => {
    const list = data?.ads || (data?.ad ? [data.ad].flat() : []);
    return Array.isArray(list) ? list : [];
  }, [data]);

  const activeAd = ads[0] || null;
  const activeType = resolveAdType(activeAd);

  const mediaItems = useMemo(() => {
    if (!activeAd) return [];
    const items = activeAd.mediaItems || [];
    const list = Array.isArray(items) ? items : items ? [items] : [];
    const primary =
      activeAd.mediaPath ||
      activeAd.image ||
      activeAd.media ||
      activeAd.media_path ||
      "";
    const merged = [];
    const pushUnique = (item) => {
      if (item == null || item === "") return;
      const key =
        typeof item === "object"
          ? item.url || item.path || item.src || JSON.stringify(item)
          : String(item);
      if (
        merged.some((m) => {
          const mk =
            typeof m === "object"
              ? m.url || m.path || m.src || JSON.stringify(m)
              : String(m);
          return mk === key;
        })
      ) {
        return;
      }
      merged.push(item);
    };
    pushUnique(primary);
    list.forEach(pushUnique);
    return merged.map(resolveMediaUrl).filter(Boolean);
  }, [activeAd]);

  useEffect(() => {
    setCarouselIndex(0);
  }, [activeAd?._id]);

  useEffect(() => {
    if (!activeAd?._id || impressedRef.current.has(activeAd._id)) return;
    // Owner viewing their own ad → do not count impressions
    if (isOwnAd(activeAd)) {
      impressedRef.current.add(activeAd._id);
      return;
    }
    impressedRef.current.add(activeAd._id);
    recordImpression({
      adId: activeAd._id,
      visitorUserId: visitorUserId || undefined,
      page: location.pathname || "",
      country: visitor.country || "",
    }).catch(() => {});
  }, [
    activeAd,
    visitorUserId,
    visitor.country,
    location.pathname,
    recordImpression,
  ]);

  const handleAdClick = async () => {
    if (!activeAd?._id) return;
    const redirect =
      activeAd?.link || activeAd?.targetUrl || activeAd?.redirectUrl || "";
    // Owner clicking their own ad → open link but do not count clicks
    if (isOwnAd(activeAd)) {
      if (redirect) window.open(redirect, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const res = await recordClick({
        adId: activeAd._id,
        visitorUserId: visitorUserId || undefined,
        country: visitor.country || "",
      }).unwrap();
      const url = res?.redirectUrl || res?.redirect || res?.url || redirect;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch {
      if (redirect) window.open(redirect, "_blank", "noopener,noreferrer");
    }
  };

  // Ads are optional: never show a loading spinner on refresh/empty.
  // Render the banner only when a real ad + media is available.
  const hasAd = Boolean(activeAd && mediaItems.length > 0);

  const renderMedia = (src, key) => {
    if (activeType === "video") {
      return (
        <video
          key={key}
          className="shop-ad-media shop-ad-media--video"
          src={src}
          controls
          muted
          playsInline
        />
      );
    }
    return (
      <img
        key={key}
        className="shop-ad-media"
        src={src}
        alt={activeAd?.description || activeAd?.title || "Sponsored ad"}
        loading="lazy"
        onError={(e) => {
          // Retry once via Node asset helper for mis-hosted creatives
          const current = e.target.src || "";
          if (!e.target.dataset.retried && current.includes("/uploads/")) {
            e.target.dataset.retried = "1";
            const file = current.split("/uploads/")[1];
            if (file) {
              e.target.src = getAsset(`/images/${file}`);
              return;
            }
          }
          e.target.onerror = null;
          e.target.src =
            "data:image/svg+xml," +
            encodeURIComponent(
              `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="200"><rect fill="#e8eef5" width="100%" height="100%"/><text x="50%" y="50%" fill="#6b7c93" font-family="sans-serif" font-size="20" text-anchor="middle" dy=".3em">Sponsored Ad</text></svg>`,
            );
        }}
      />
    );
  };

  if (!hasAd) {
    if (props.hideIfEmpty) {
      return null;
    }

    const handleCreateAdClick = () => {
      if (props.shopid) {
        navigate(`/promote-shop/${props.shopid}`);
      } else if (props.serviceid) {
        navigate(`/promote-service/${props.serviceid}`);
      } else {
        navigate("/marketing-profile");
      }
    };

    return (
      <Row className="mb-3">
        <Col sm={12} md={12} lg={12} xl={12} className="px-2 px-md-3">
          <div className="shop-ad-wrap rounded bg-white mt-2 w-100">
            <div className="shop-ad-empty-banner text-center d-flex flex-column align-items-center justify-content-center p-4">
              <div className="shop-ad-empty-icon mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m3 11 18-5v12L3 14v-3z" />
                  <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
                </svg>
              </div>
              <h6 className="shop-ad-empty-title mb-1">No Active Banner Ads</h6>
              <p className="shop-ad-empty-desc mb-3">
                Target your audience & boost shop visibility across the platform
                with custom banner campaigns.
              </p>
            </div>
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row className="mb-3">
        <Col sm={12} md={12} lg={12} xl={12} className="px-2 px-md-3">
          <div className="shop-ad-wrap rounded bg-white mt-2 w-100">
            <div className="shop-ad-banner position-relative">
              {activeType === "carousel" && mediaItems.length > 1 ? (
                <Carousel
                  activeIndex={carouselIndex}
                  onSelect={(idx) => setCarouselIndex(idx)}
                  interval={4000}
                  indicators={mediaItems.length > 1}
                  controls={mediaItems.length > 1}
                  className="shop-ad-carousel"
                  touch
                >
                  {mediaItems.map((src, idx) => (
                    <Carousel.Item key={`${activeAd._id}-${idx}`}>
                      <button
                        type="button"
                        className="shop-ad-hit border-0 p-0 w-100 bg-transparent"
                        onClick={handleAdClick}
                        aria-label={`${activeAd.title || "Sponsored"} slide ${idx + 1}`}
                      >
                        {renderMedia(src, idx)}
                      </button>
                    </Carousel.Item>
                  ))}
                </Carousel>
              ) : (
                <button
                  type="button"
                  className="shop-ad-hit border-0 p-0 w-100 bg-transparent"
                  onClick={handleAdClick}
                  aria-label={activeAd.title || "Sponsored ad"}
                >
                  {renderMedia(mediaItems[0], "single")}
                </button>
              )}
              <span className="shop-ad-label">
                {activeType === "carousel"
                  ? "Sponsored · Carousel"
                  : activeType === "video"
                    ? "Sponsored · Video"
                    : "Sponsored"}
              </span>
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default ShopAdds;
